import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import HeroTransition from '../components/HeroTransition';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { logActivity } from '../utils/sessionManager';
import OnScreenKeyboard from '../components/OnScreenKeyboard';
import Modal from '../components/Modal';
import config from '../config';
import './PhotoCapture.css';

const PhotoCapture = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { transition, superhero } = location.state || {};
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const emailInputRef = useRef(null);
  const [countdown, setCountdown] = useState(null);
  const [showPostCapture, setShowPostCapture] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [modalAgreedToTerms, setModalAgreedToTerms] = useState(false);
  const hasLoggedPageVisit = useRef(false);
  const hasLoggedCameraStart = useRef(false);

  // Stop camera function
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('Camera track stopped:', track.label);
      });
      streamRef.current = null;
      setCameraReady(false);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  // Add navigation warning with modal
  const { NavigationWarningModal } = useNavigationWarning(true, stopCamera);

  // Log page visit only once
  useEffect(() => {
    if (!hasLoggedPageVisit.current) {
      logActivity('photo_capture_visited', { superhero: superhero?.name });
      hasLoggedPageVisit.current = true;
    }
  }, [superhero?.name]);

  // Start camera function
  const startCamera = async () => {
    try {
      // PRODUCTION MODE OR DEVELOPMENT MODE ON LOCALHOST: Use real camera
      // All other places: Simulate camera ready state
      const shouldUseRealCamera = process.env.NODE_ENV === 'production' || (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost');

      if (!shouldUseRealCamera) {
        console.log('Using simulated camera (dev mode not on localhost)');
        setCameraReady(true);
        setError(null);
        if (!hasLoggedCameraStart.current) {
          logActivity('camera_started');
          hasLoggedCameraStart.current = true;
        }
        return;
      }

      if (streamRef.current) {
        console.log('Camera already running');
        return;
      }

      setCameraReady(false);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }
      });

      streamRef.current = mediaStream;
      setError(null);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          setCameraReady(true);
          if (!hasLoggedCameraStart.current) {
            logActivity('camera_started');
            hasLoggedCameraStart.current = true;
          }
        };
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      let errorMessage = 'Unable to access camera. ';

      if (err.name === 'NotAllowedError') {
        errorMessage += 'Please allow camera access when prompted by your browser.';
      } else if (err.name === 'NotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage += 'Camera is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        errorMessage += 'Camera does not support the required settings.';
      } else {
        errorMessage += 'Please check your browser settings and try again.';
      }

      setError(errorMessage);
      setCameraReady(false);
      logActivity('camera_error', { error: err.name });
    }
  };

  // Auto-start camera on component mount
  useEffect(() => {
    startCamera();

    return () => {
      console.log('PhotoCapture unmounting - stopping camera');
      stopCamera();
    };
  }, []);

  const startCaptureCountdown = () => {
    if (cameraReady && countdown === null) {
      setCountdown(3);
      logActivity('capture_countdown_started');
    }
  };

  const capturePhoto = useCallback(() => {
    if (cameraReady) {
      // SIMULATION MODE: Generate a black screen image instead of capturing from camera
      // Use real camera in production or dev mode on localhost
      const shouldUseRealCamera = process.env.NODE_ENV === 'production' || (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost');

      if (!shouldUseRealCamera) {
        console.log('Using simulated capture (dev mode not on localhost)');
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Set canvas to a standard size
        canvas.width = 640;
        canvas.height = 480;

        // Fill with black
        context.fillStyle = '#000000';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Add some text to indicate it's a dev placeholder
        context.fillStyle = '#FFFFFF';
        context.font = '24px Arial';
        context.textAlign = 'center';
        context.fillText('SIMULATION MODE', canvas.width / 2, canvas.height / 2 - 20);
        context.fillText('Placeholder Image', canvas.width / 2, canvas.height / 2 + 20);

        const imageDataUrl = canvas.toDataURL('image/jpeg');
        setCapturedImage(imageDataUrl);

        // Trigger the post-capture animation after a short delay
        setTimeout(() => {
          setShowPostCapture(true);
        }, 300);

        logActivity('photo_captured');
        return;
      }

      // PRODUCTION MODE: Capture from actual camera
      if (videoRef.current && canvasRef.current) {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Apply horizontal flip to match the mirrored camera preview
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageDataUrl = canvas.toDataURL('image/jpeg');
        setCapturedImage(imageDataUrl);

        // Stop the camera immediately after capturing
        stopCamera();

        // Trigger the post-capture animation after a short delay
        setTimeout(() => {
          setShowPostCapture(true);
        }, 300);

        logActivity('photo_captured');
      }
    }
  }, [cameraReady]);

  // Countdown timer effect
  useEffect(() => {
    if (countdown === null) return;

    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      capturePhoto();
      setCountdown(null);
    }
  }, [countdown, capturePhoto]);

  const retakePhoto = () => {
    setCapturedImage(null);
    setShowPostCapture(false);
    setError(null);
    setEmail('');
    setEmailError('');
    setAgreedToTerms(false);
    hasLoggedCameraStart.current = false;
    startCamera();
    logActivity('photo_retake');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleContinue = () => {
    // Check what requirements are missing
    const missingRequirements = [];

    if (!email || emailError) {
      missingRequirements.push('enter a valid email address');
    }

    if (!agreedToTerms) {
      missingRequirements.push('accept the terms and conditions');
    }

    if (missingRequirements.length > 0) {
      // If only terms are missing, show terms modal
      if (missingRequirements.length === 1 && missingRequirements[0] === 'accept the terms and conditions') {
        setModalAgreedToTerms(false);
        setShowTermsModal(true);
        return;
      }

      // Show validation modal with missing requirements
      const message = `Please ${missingRequirements.join(' and ')} to continue.`;
      setValidationMessage(message);
      setShowValidationModal(true);
      return;
    }

    // All requirements met, proceed
    stopCamera();

    logActivity('email_entered', { email, superhero: superhero?.name });

    navigate('/select-pose', {
      state: {
        superhero,
        userPhoto: capturedImage,
        email
      }
    });
  };

  const handleHomeClick = () => {
    stopCamera();
    logActivity('navigation_home_from_photo');
    navigate('/', { replace: true });
  };

  const handleBackClick = () => {
    stopCamera();
    logActivity('navigation_back_from_photo');
    navigate('/select-superhero');
  };

  const handleKeyboardInput = useCallback((key) => {
    if (key === 'Backspace') {
      setEmail(prev => prev.slice(0, -1));
    } else if (key === 'OK') {
      setShowKeyboard(false);
    } else if (key === 'Space') {
      setEmail(prev => prev + ' ');
    } else {
      setEmail(prev => prev + key);
    }

    // Validate after each key press
    const newEmail = key === 'Backspace' ? email.slice(0, -1) : email + (key === 'Space' ? ' ' : key);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  }, [email]);

  const handleShowKeyboard = () => {
    setShowKeyboard(true);
    logActivity('onscreen_keyboard_opened');
  };

  const handleCloseKeyboard = () => {
    setShowKeyboard(false);
    logActivity('onscreen_keyboard_closed');
  };

  const handleEmailFocus = () => {
    if (config.enableOnScreenKeyboard) {
      setShowKeyboard(true);
    }
  };

  const handleEmailBlur = (e) => {
    // Prevent blur when clicking on keyboard buttons
    // Only allow blur if clicking outside both input and keyboard
    if (e?.relatedTarget?.closest('.onscreen-keyboard')) {
      e.preventDefault();
      if (emailInputRef.current) {
        emailInputRef.current.focus();
      }
    }
  };

  // Handle physical keyboard input when on-screen keyboard is active
  useEffect(() => {
    if (!showKeyboard) return;

    const handleKeyDown = (event) => {
      // Keep focus on input field
      if (emailInputRef.current && document.activeElement !== emailInputRef.current) {
        emailInputRef.current.focus();
      }

      // Prevent default behavior for certain keys to avoid conflicts
      if (event.key === 'Enter') {
        event.preventDefault();
        handleKeyboardInput('OK');
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        handleKeyboardInput('Backspace');
      } else if (event.key === ' ') {
        event.preventDefault();
        handleKeyboardInput('Space');
      } else if (event.key.length === 1) {
        // Allow single character keys (letters, numbers, symbols)
        event.preventDefault();
        handleKeyboardInput(event.key);
      }
      // Ignore other keys like Shift, Ctrl, etc.
    };

    // Ensure input field gets focus when keyboard opens
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showKeyboard, handleKeyboardInput]);

  const content = (
    <div className="photo-capture">
      <div className="photo-background"></div>

      {NavigationWarningModal}

      <motion.button
        className="back-button"
        onClick={handleBackClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: transition ? 0.5 : 0 }}
      >
        ← BACK
      </motion.button>

      <motion.button
        className="home-button"
        onClick={handleHomeClick}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: transition ? 0.5 : 0 }}
      >
        HOME
      </motion.button>

      <div className="content-container">
        <AnimatePresence mode="wait">
          {!capturedImage ? (
            <motion.h1
              key="capture-title"
              className="page-title"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              transition={{ duration: 0.6, delay: transition ? 0.5 : 0 }}
            >
              CAPTURE YOUR PHOTO
            </motion.h1>
          ) : (
            <motion.h1
              key="review-title"
              className="page-title"
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              transition={{ duration: 0.6 }}
            >
              SELECTED HERO: {superhero?.name?.toUpperCase() || 'SUPERHERO'}
            </motion.h1>
          )}
        </AnimatePresence>

        {/* Countdown positioned above camera but absolutely positioned to not affect layout */}
        <AnimatePresence>
          {countdown !== null && (
            <motion.div
              className="countdown-above-camera"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.5 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="countdown-number"
                key={countdown}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 2, opacity: 0 }}
                transition={{ duration: 0.8 }}
              >
                {countdown}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Camera view - no sliding during countdown, smooth exit after capture */}
        <AnimatePresence>
          {!capturedImage && (
            <motion.div
              className="camera-container"
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              exit={capturedImage !== null ? {
                scale: 0.9,
                opacity: 0
              } : false}
              transition={{
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
                exit: { duration: 0.4, ease: [0.4, 0, 0.2, 1] }
              }}
            >

              {error ? (
                <div className="error-message">{error}</div>
              ) : (
                <>
                  {(() => {
                    const shouldUseRealCamera = process.env.NODE_ENV === 'production' || (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost');
                    return !shouldUseRealCamera ? (
                      <div
                        className="camera-feed"
                        style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#000000',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          color: '#FFFFFF',
                          fontSize: '24px',
                          fontFamily: 'Arial, sans-serif'
                        }}
                      >
                        <div>SIMULATION MODE</div>
                        <div style={{ fontSize: '18px', marginTop: '10px' }}>Camera Placeholder</div>
                      </div>
                    ) : (
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="camera-feed"
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    );
                  })()}
                  <div className="camera-alignment-note">
                    {(() => {
                      const shouldUseRealCamera = process.env.NODE_ENV === 'production' || (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost');
                      return !shouldUseRealCamera
                        ? 'Simulation mode - click capture to continue'
                        : 'Align your face in the center for the best results';
                    })()}
                  </div>
                </>
              )}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Post-capture cards with smooth animations */}
        <AnimatePresence>
          {capturedImage && showPostCapture && (
            <motion.div 
              className="post-capture-container"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Left card - Captured Photo */}
              <motion.div
                className="result-card left-card"
                initial={{ y: 30, opacity: 0, scale: 0.95 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.6,
                  ease: [0.34, 1.56, 0.64, 1],
                  delay: 0.1
                }}
              >
                <div className="card-header">
                  <h3 className="card-title">Your Photo</h3>
                </div>
                <div className="card-content">
                  <motion.div
                    className="captured-photo-section"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  >
                    <img
                      src={capturedImage}
                      alt="Your captured photo"
                      className="captured-photo"
                    />
                  </motion.div>
                  <motion.div
                    className="photo-actions"
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                  >
                    <p className="retake-note">Not happy with your photo?</p>
                    <motion.button
                      className="retake-button"
                      onClick={retakePhoto}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      RETAKE PHOTO
                    </motion.button>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right card - Email Entry */}
              <motion.div 
                className="result-card right-card"
                initial={{ y: 30, opacity: 0, scale: 0.95 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.34, 1.56, 0.64, 1],
                  delay: 0.25
                }}
              >
                <div className="card-header">
                  <h3 className="card-title">We'll send your superhero image to this email</h3>
                </div>
                <div className="card-content">
                  <motion.div
                    className="email-input-container"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.5 }}
                  >
                    <input
                      ref={emailInputRef}
                      type="email"
                      className={`email-input ${emailError ? 'error' : ''}`}
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={handleEmailChange}
                      onFocus={handleEmailFocus}
                      onClick={handleEmailFocus}
                      onBlur={handleEmailBlur}
                    />
                    {config.enableOnScreenKeyboard && (
                      <motion.button
                        className="keyboard-button"
                        onClick={handleShowKeyboard}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        title="Open on-screen keyboard"
                      >
                        ⌨️
                      </motion.button>
                    )}
                  </motion.div>
                  {emailError && <p className="email-error">{emailError}</p>}

                  <motion.div 
                    className="terms-container"
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.0, duration: 0.5 }}
                  >
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                      />
                      <span className="checkbox-text">
                        <strong>Terms and Conditions:</strong><br/>
                        By providing your email and checking this box, you consent to receive your generated superhero image via email.
                        Your email address will be shared with the show organizer for event follow-up purposes.
                        Your captured photo and generated image will not be stored, saved, or used for any other purposes beyond creating and delivering your superhero image.
                        This service is provided for entertainment purposes at the trade show event only.
                      </span>
                    </label>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="privacy-note">Note: We do not store any of your photos. They are used only to create your superhero image.</p>

        <div className="button-container">
          {!capturedImage ? (
            <motion.button
              className={`capture-button ${(!cameraReady || countdown !== null) ? 'disabled' : ''}`}
              onClick={startCaptureCountdown}
              disabled={!cameraReady || countdown !== null}
              whileHover={(cameraReady && countdown === null) ? { scale: 1.05 } : {}}
              whileTap={(cameraReady && countdown === null) ? { scale: 0.95 } : {}}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: (transition ? 0.5 : 0) + 0.6 }}
            >
              {countdown !== null ? `CAPTURING IN ${countdown}...` : cameraReady ? 'CAPTURE PHOTO' : 'LOADING CAMERA...'}
            </motion.button>
          ) : (
            <motion.button
              className="continue-button"
              onClick={handleContinue}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              CONTINUE
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );

  if (transition) {
    return (
      <>
        <HeroTransition transitionType={transition}>
          {content}
        </HeroTransition>
        {showKeyboard && config.enableOnScreenKeyboard && (
          <OnScreenKeyboard
            onKeyPress={handleKeyboardInput}
            onClose={handleCloseKeyboard}
            currentText={email}
          />
        )}
        <Modal
          isOpen={showValidationModal}
          title="REQUIRED INFORMATION"
          message={validationMessage}
          onConfirm={() => setShowValidationModal(false)}
          confirmText="OK"
        />
        <Modal
          isOpen={showTermsModal}
          title="ACCEPT TERMS AND CONDITIONS"
          onConfirm={() => {
            setAgreedToTerms(true);
            setShowTermsModal(false);
            // Automatically proceed since terms are now accepted
            stopCamera();
            logActivity('email_entered', { email, superhero: superhero?.name });
            navigate('/select-pose', {
              state: {
                superhero,
                userPhoto: capturedImage,
                email
              }
            });
          }}
          onCancel={() => setShowTermsModal(false)}
          confirmText="ACCEPT"
          cancelText="CANCEL"
          showCancel={true}
        >
          <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
            <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>
              Please accept the terms and conditions to continue:
            </p>
            <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={modalAgreedToTerms}
                onChange={(e) => setModalAgreedToTerms(e.target.checked)}
                style={{ marginRight: '10px', marginTop: '2px', transform: 'scale(1.2)' }}
              />
              <span style={{ fontSize: '0.9em' }}>
                <strong>Terms and Conditions:</strong><br/>
                By providing your email and checking this box, you consent to receive your generated superhero image via email.
                Your email address will be shared with the show organizer for event follow-up purposes.
                Your captured photo and generated image will not be stored, saved, or used for any other purposes beyond creating and delivering your superhero image.
                This service is provided for entertainment purposes at the trade show event only.
              </span>
            </label>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      {content}
      {showKeyboard && config.enableOnScreenKeyboard && (
        <OnScreenKeyboard
          onKeyPress={handleKeyboardInput}
          onClose={handleCloseKeyboard}
          currentText={email}
        />
      )}
      <Modal
        isOpen={showValidationModal}
        title="REQUIRED INFORMATION"
        message={validationMessage}
        onConfirm={() => setShowValidationModal(false)}
        confirmText="OK"
      />
      <Modal
        isOpen={showTermsModal}
        title="ACCEPT TERMS AND CONDITIONS"
        onConfirm={() => {
          setAgreedToTerms(true);
          setShowTermsModal(false);
          // Automatically proceed since terms are now accepted
          stopCamera();
          logActivity('email_entered', { email, superhero: superhero?.name });
          navigate('/select-pose', {
            state: {
              superhero,
              userPhoto: capturedImage,
              email
            }
          });
        }}
        onCancel={() => setShowTermsModal(false)}
        confirmText="ACCEPT"
        cancelText="CANCEL"
        showCancel={true}
      >
        <div style={{ textAlign: 'left', lineHeight: '1.6' }}>
          <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>
            Please accept the terms and conditions to continue:
          </p>
          <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={modalAgreedToTerms}
              onChange={(e) => setModalAgreedToTerms(e.target.checked)}
              style={{ marginRight: '10px', marginTop: '2px', transform: 'scale(1.2)' }}
            />
            <span style={{ fontSize: '0.9em' }}>
              <strong>Terms and Conditions:</strong><br/>
              By providing your email and checking this box, you consent to receive your generated superhero image via email.
              Your email address will be shared with the show organizer for event follow-up purposes.
              Your captured photo and generated image will not be stored, saved, or used for any other purposes beyond creating and delivering your superhero image.
              This service is provided for entertainment purposes at the trade show event only.
            </span>
          </label>
        </div>
      </Modal>
    </>
  );
};

export default PhotoCapture;
